/**
 * geoTokenStore.ts
 *
 * Module-level singleton that holds the current geospatial-access token minted
 * by the BFF GeoSessionService (POST /drt/api/bff/geo-session).
 *
 * The token is stored ONLY in memory — never in localStorage, sessionStorage, or
 * cookies.  The BFF response is marked no-store, so neither the browser nor any
 * proxy caches the credential.
 *
 * Design rationale:
 *   The Axios apiClient interceptor runs outside React's render cycle and
 *   therefore cannot read from a React context.  A module-level store is the
 *   standard pattern used across the codebase (e.g. VehiclePositionStreamProvider
 *   reads the projection token from a module var before opening the SSE stream).
 *
 *   GeoSessionProvider writes the token here immediately after a successful mint
 *   and clears it on unmount (logout).  The interceptor in client.ts reads it
 *   synchronously on every /api/v1/* request.
 */

/**
 * Window event fired by the apiClient response interceptor when a /api/v1/*
 * call returns 401 (geo token expired or revoked early). GeoSessionProvider
 * listens for it and re-mints. Declared here rather than in client.ts so both
 * the producer and the consumer import the name from one place.
 */
export const GEO_TOKEN_EXPIRED_EVENT = 'geo:token-expired';

/** Current raw geo-session token, or null when not yet minted / after logout */
let _geoToken: string | null = null;

/**
 * Tenant ID associated with the current geo-session token.
 * The maps proxy validates X-Tenant-ID against the tenantId embedded in the
 * GeoSessionRecord (set from BFF session context at mint time). Both must match.
 */
let _geoTenantId: string | null = null;

/** Write the token and tenant after a successful BFF mint */
export const setGeoToken = (token: string): void => {
  _geoToken = token;
};

/** Store the tenantId alongside the token for use in the X-Tenant-ID header */
export const setGeoTenantId = (tenantId: string): void => {
  _geoTenantId = tenantId;
};

/** Read the current token (called by the apiClient request interceptor) */
export const getGeoToken = (): string | null => _geoToken;

/** Read the tenant ID to attach as X-Tenant-ID on /api/v1/* requests */
export const getGeoTenantId = (): string | null => _geoTenantId;

/** Clear both token and tenantId on logout or provider unmount */
export const clearGeoToken = (): void => {
  _geoToken = null;
  _geoTenantId = null;
};
